import React, { useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { constants } from "../assets/constants";
import { AuthContext } from "../contexts";
import Select from "react-select";
import { CustomOption } from ".";
import { debounce } from "../utils";
import { components } from "react-select";

const normalizeString = (str) => {
    if (!str) return "";
    const withoutAccents = str
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    return withoutAccents.replace(/[-_.,;:]/g, " ");
};

const CustomSearchInput = React.forwardRef(({ placeholder, entity, isMultiSearch, isClearable, labelType, onChange, className, value, Option, filterOption, noIcon }, ref) => {
    const [items, setItems] = useState();
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [isNavigating, setIsNavigating] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [menuPlacement, setMenuPlacement] = useState("bottom");
    const [menuAlign, setMenuAlign] = useState("left");
    const { token } = useContext(AuthContext);
    const selectContainerRef = useRef(null);

    const scrollToOption = (index) => {
        setTimeout(() => {
            const menuList = document.querySelector(".select__menu-list");
            const options = menuList?.querySelectorAll(".c-badge-container");

            if (menuList && options && options[index]) {
                const option = options[index];
                const menuRect = menuList.getBoundingClientRect();
                const optionRect = option.getBoundingClientRect();

                if (optionRect.bottom > menuRect.bottom) {
                    option.scrollIntoView({ block: "end", behavior: "smooth" });
                } else if (optionRect.top < menuRect.top) {
                    option.scrollIntoView({ block: "start", behavior: "smooth" });
                }
            }
        }, 10);
    };

    const formatItems = useCallback(
        (items) => {
            return items.map((item) => {
                if (item.__isSeparator) {
                    return { ...item, value: "__separator__", label: "", isDisabled: true };
                }
                item.value = item.id;
                switch (labelType) {
                    case "user":
                        item.value = item.id ? item.id : item.lastName;
                        item.label = item.firstName + " " + item.lastName;
                        break;
                    case "address":
                        item.label = item.address;
                        item.value = item.id;
                        break;
                    case "tax":
                        item.label = item.tax_name;
                        item.value = item.tax_id;
                        break;
                    case "contact_person":
                        item.id = item.contact_person_id;
                        item.key = item.contact_person_id;
                        item.label = item.email;
                        item.value = item.contact_person_id;
                        break;
                    case "invoice":
                        item.isInvoice = true;
                        item.label = item.data.invoice_number;
                        item.value = item.id;
                        break;
                    case "engineGroup":
                        item.label = "Groupe " + item.id + " (" + item.dedgeId + " - " + item.consumerKey + ")";
                        item.value = item.id;
                        break;
                    case "constant":
                        break;
                    case "language":
                        item.label = `${item.language} (${item.code})`;
                        break;
                    case "filter":
                        const foundConstant = constants.all.find((e) => {
                            return e.value === item.label;
                        });
                        item.label = foundConstant ? foundConstant.label : item.label;
                        break;
                    default:
                        item.label = item.name;
                        break;
                }
                return item;
            });
        },
        [labelType]
    );

    const debouncedFetch = useCallback(
        debounce((entity, token) => {
            setIsLoading(true);
            axios
                .get(process.env.REACT_APP_UPA_API_HOST + entity, {
                    headers: { Authorization: "Bearer " + token },
                })
                .then((response) => {
                    const formattedItems = formatItems(response.data);
                    setItems(formattedItems);
                    setIsLoading(false);
                })
                .catch((error) => {
                    console.log(error);
                    setIsLoading(false);
                });
        }, 300),
        [formatItems]
    );

    useEffect(() => {
        if (typeof entity === "string") {
            debouncedFetch(entity, token);
        } else {
            const formattedItems = formatItems(entity);
            setItems(formattedItems);
        }
    }, [entity, token, formatItems, debouncedFetch]);

    useEffect(() => {
        const handleDataUpdate = () => {
            if (typeof entity === "string") {
                debouncedFetch(entity, token);
            }
        };

        window.addEventListener("groupedSupportUpdated", handleDataUpdate);

        return () => {
            window.removeEventListener("groupedSupportUpdated", handleDataUpdate);
        };
    }, [entity, token, debouncedFetch]);

    const defaultFilterOption = (option, inputValue) => {
        const optionLabel = normalizeString(option.label || "");
        const optionReference = normalizeString(option.data?.reference || "");
        const input = normalizeString(inputValue || "");
        return optionLabel.includes(input) || optionReference.includes(input);
    };

    const handleKeyDown = (event) => {
        if (!isMenuOpen) return;

        if (event.key === "ArrowDown") {
            event.preventDefault();
            setIsNavigating(true);
            setFocusedIndex((prev) => {
                const maxIndex = (items?.length || 1) - 1;
                const newIndex = prev < maxIndex ? prev + 1 : prev;
                if (items && items[newIndex] && newIndex !== prev) {
                    setInputValue(items[newIndex].label);
                    scrollToOption(newIndex);
                }
                return newIndex;
            });
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setIsNavigating(true);
            setFocusedIndex((prev) => {
                const newIndex = prev > 0 ? prev - 1 : prev;
                if (items && items[newIndex] && newIndex !== prev) {
                    setInputValue(items[newIndex].label);
                    scrollToOption(newIndex);
                }
                return newIndex;
            });
        } else if (event.key === "Enter") {
            event.preventDefault();
            if (focusedIndex >= 0 && items && items[focusedIndex]) {
                onChange(items[focusedIndex]);
                handleMenuClose();
            }
        } else if (event.key === "Escape") {
            event.preventDefault();
            handleMenuClose();
        }
    };

    const getStatusVariant = (engineType) => {
        if (engineType === "dedge") return "purple";
        if (engineType === "reservit") return "warning";
        if (engineType === "mews") return "blue";
        if (engineType === "mister_booking") return "success";
        if (engineType === "amenitiz") return "info";
        if (engineType === "brit_hotels") return "success";
        if (engineType === "namastay") return "purple";
        if (engineType === "synxis") return "warning";
        if (engineType === "cloudbeds") return "blue";
        if (engineType === "thais") return "yellow";
        if (engineType === "siteminder") return "purple";
        if (engineType === "best_western") return "info";
        if (engineType === "the_originals") return "yellow";
        if (engineType === "other") return "default";
        return "default";
    };

    const CustomSingleValue = useMemo(
        () => (props) => {
            const { data } = props;
            let additionalClass = "";

            if (data?.value) {
                if (labelType === "constant" && entity && Array.isArray(entity)) {
                    const isEngineType = entity.some((item) => item.value === "dedge" || item.value === "reservit" || item.value === "mews" || item.value === "mister_booking" || item.value === "amenitiz" || item.value === "brit_hotels" || item.value === "namastay" || item.value === "synxis" || item.value === "cloudbeds" || item.value === "thais" || item.value === "siteminder" || item.value === "best_western" || item.value === "the_originals" || item.value === "other");

                    if (isEngineType) {
                        additionalClass = ` ${getStatusVariant(data.value)}`;
                    } else {
                        additionalClass = ` ${data.value}`;
                    }
                } else {
                    additionalClass = ` ${data.value}`;
                }
            }

            return (
                <components.SingleValue {...props} className={`select__single-value${additionalClass}`}>
                    {props.children}
                </components.SingleValue>
            );
        },
        [labelType, entity]
    );

    const CustomLoadingMessage = useMemo(
        () => () =>
            (
                <div className="select__loading">
                    <div className="loading-spinner"></div>
                    <span>Chargement...</span>
                </div>
            ),
        []
    );

    const CustomMenu = useMemo(
        () => (props) => {
            return (
                <components.Menu {...props} className={`${props.className || ""} ${isClosing ? "menu-closing" : ""}`}>
                    {props.children}
                </components.Menu>
            );
        },
        [isClosing]
    );

    const handleMenuClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsMenuOpen(false);
            setFocusedIndex(-1);
            setInputValue("");
            setIsNavigating(false);
            setIsClosing(false);
        }, 150);
    };

    return (
        <div className="wrapper-search-input" ref={selectContainerRef}>
        <Select
            ref={ref}
            isMulti={isMultiSearch}
            name="colors"
            isRequired={true}
            options={items}
            isClearable={isClearable}
            isLoading={isLoading}
            onChange={(selectedOption) => {
                onChange(selectedOption);
                handleMenuClose();
            }}
            value={value}
            className={`${isMultiSearch ? "basic-multi-select multi-search-input" : "basic-single single-search-input"} ${noIcon ? "no-icon" : ""} ${className || ""}`}
            classNamePrefix="select"
            placeholder={placeholder}
            styles={{
                menu: (base) => ({
                    ...base,
                    ...(menuAlign === "right" && { right: 0, left: "auto" }),
                }),
            }}
            components={useMemo(
                () => ({
                    Option: (props) => {
                        if (props.data.__isSeparator) {
                            return <div className="select__separator" />;
                        }
                        const isFirstRecent = props.data.__isRecent && items?.findIndex((i) => i.__isRecent) === items?.indexOf(props.data);
                        const CustomOptionComponent = Option ? Option : CustomOption;
                        return (
                            <>
                                {isFirstRecent && <div className="select__group-header">Récents</div>}
                                <CustomOptionComponent {...props} isFocused={props.data === items?.[focusedIndex]} className={`${props.data === items?.[focusedIndex] ? "focused" : ""}`} />
                            </>
                        );
                    },
                    SingleValue: CustomSingleValue,
                    LoadingMessage: CustomLoadingMessage,
                    Menu: CustomMenu,
                }),
                [Option, items, focusedIndex, CustomSingleValue, CustomLoadingMessage, CustomMenu]
            )}
            menuPortalTarget={className?.includes("modal-select") || className?.includes("entry-line") ? document.body : undefined}
            menuPosition={className?.includes("modal-select") || className?.includes("entry-line") ? "fixed" : "absolute"}
            menuShouldBlockScroll={className?.includes("modal-select") || className?.includes("entry-line")}
            menuPlacement={menuPlacement}
            // filterOption={filterOption || defaultFilterOption}
            isSearchable={true}
            openMenuOnFocus={true}
            tabSelectsValue={false}
            escapeClearsValue={false}
            blurInputOnSelect={true}
            backspaceRemovesValue={true}
            captureMenuScroll={false}
            autoFocus={false}
            defaultMenuIsOpen={false}
            menuIsOpen={isMenuOpen}
            onMenuOpen={() => {
                const controlEl = selectContainerRef.current?.querySelector(".select__control");
                if (controlEl) {
                    const rect = controlEl.getBoundingClientRect();
                    const spaceBelow = window.innerHeight - rect.bottom;
                    const spaceRight = window.innerWidth - rect.left;
                    setMenuPlacement(spaceBelow < 250 ? "top" : "bottom");
                    setMenuAlign(spaceRight < 300 ? "right" : "left");
                }
                setIsMenuOpen(true);
                setFocusedIndex(-1);
            }}
            onMenuClose={handleMenuClose}
            onKeyDown={handleKeyDown}
            inputValue={inputValue}
            onInputChange={(newValue, actionMeta) => {
                if (actionMeta.action === "input-change") {
                    setInputValue(newValue);
                    setIsNavigating(false);
                    setFocusedIndex(-1);
                }
            }}
            filterOption={(option, inputValue) => {
                if (isNavigating) {
                    return true;
                }
                return (filterOption || defaultFilterOption)(option, inputValue);
            }}
            getOptionValue={(option) => option.value || option.id}
            getOptionLabel={(option) => option.label || option.name || ""}
        />
        </div>
    );
});

export default CustomSearchInput;
